import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import PersonRemoveOutlinedIcon from '@mui/icons-material/PersonRemoveOutlined';
import {
  appModalActionsSx,
  appModalCloseButtonSx,
  appModalContentSx,
  appModalHeaderDividerSx,
  appModalSecondaryButtonSx,
  appModalSubtitleSx,
  appModalTitleBarSx,
  appModalTitleSx,
} from '../../../Components/appModalChrome.styles';
import { confirmDialog } from '../../../Components/ConfirmDialog';
import { toast } from '../../../Components/Toast';
import { getScrollEdgeFadeSx, useScrollEdgeFade } from '../../../hooks/useScrollEdgeFade';
import { colors } from '../../../styles/theme';
import {
  SPACE_NAME_MAX,
  deleteSpaceMember,
  getMySpace,
  getSpaceMembers,
  getSpaceOwnerLabel,
  inviteSpaceMember,
  readSpaceMembers,
  readSpaceProfile,
  upsertMySpace,
} from '../../../services/spaceMembersService';
import {
  emptyStateSx,
  fieldRowSx,
  hiddenScrollbarSx,
  inlineActionButtonSx,
  listWellSx,
  memberAvatarSx,
  memberCardSx,
  memberMetaSx,
  memberNameSx,
  nameHelperTextProps,
  removeButtonSx,
  requiredCaptionSx,
  sectionLabelRowSx,
  sectionLabelSx,
  skeletonSx,
  spaceFieldSx,
  spaceMembersPaperSx,
} from './SpaceMembersDialog.styles';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function memberKey(member) {
  return String(member?.email || member?.user_id || '').trim();
}

function memberInitial(member) {
  const source = memberKey(member);
  const local = source.includes('@') ? source.split('@')[0] : source;
  return (local.charAt(0) || '?').toUpperCase();
}

function MembersSkeleton() {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }} aria-hidden>
      {[0, 1, 2].map((key) => (
        <Skeleton key={key} variant="rounded" height={56} sx={skeletonSx} />
      ))}
    </Box>
  );
}

/**
 * Gestión de invitados + nombre del espacio propio (owner).
 * Abrir desde el chip SpaceHeaderControl → “Configurar mi espacio”.
 *
 * @param {{ open: boolean, onClose?: Function, onSpaceProfileSaved?: Function }} props
 */
export default function SpaceMembersDialog({ open, onClose, onSpaceProfileSaved }) {
  const [members, setMembers] = useState([]);
  const [profile, setProfile] = useState(null);
  const [spaceNameDraft, setSpaceNameDraft] = useState('');
  const [loading, setLoading] = useState(false);
  const [savingName, setSavingName] = useState(false);
  const [email, setEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [removingKey, setRemovingKey] = useState(null);
  const { scrollRef, fadeTop, fadeBottom, refreshScrollEdges } = useScrollEdgeFade();

  const busy = inviting || Boolean(removingKey) || savingName;

  const reload = useCallback(async (signal) => {
    setLoading(true);
    try {
      const [membersRes, spaceRes] = await Promise.all([
        getSpaceMembers({ signal }),
        getMySpace({ signal }),
      ]);
      if (signal?.aborted) return;

      if (!membersRes._httpOk) {
        toast.error(membersRes?.message || 'No se pudieron cargar las personas del espacio.');
        setMembers([]);
      } else {
        setMembers(readSpaceMembers(membersRes));
      }

      if (spaceRes._httpOk) {
        const next = readSpaceProfile(spaceRes);
        setProfile(next);
        setSpaceNameDraft(String(next.space_name || ''));
      } else if (membersRes._httpOk) {
        const next = readSpaceProfile(membersRes);
        setProfile(next);
        setSpaceNameDraft(String(next.space_name || ''));
      } else {
        setProfile(null);
        setSpaceNameDraft('');
      }
    } catch (error) {
      if (error?.name === 'AbortError') return;
      console.error('Espacios: carga Personas del espacio falló', error);
      toast.error('No se pudo hablar con el servidor.');
      setMembers([]);
      setProfile(null);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    setLoading(true);
    const controller = new AbortController();
    reload(controller.signal);
    return () => controller.abort();
  }, [open, reload]);

  useEffect(() => {
    refreshScrollEdges();
  }, [members, loading, refreshScrollEdges]);

  const handleClose = () => {
    if (busy) return;
    setEmail('');
    onClose?.();
  };

  const savedSpaceName = String(profile?.space_name || '').trim();
  const nameDraft = spaceNameDraft.trim();
  const nameChanged = nameDraft !== savedSpaceName;
  const hasSpaceName = savedSpaceName.length > 0;
  /** Guardar solo con un nombre no vacío y distinto del que ya está grabado. */
  const canSaveName = nameChanged
    && nameDraft.length > 0
    && !loading
    && !savingName
    && spaceNameDraft.length <= SPACE_NAME_MAX;

  const inviteEmail = email.trim().toLowerCase();
  const isInviteEmailValid = EMAIL_RE.test(inviteEmail);
  /** Invitar con email válido y un nombre de espacio ya guardado. */
  const canInvite = hasSpaceName
    && isInviteEmailValid
    && !inviting
    && !loading
    && !savingName;
  /** Un solo aviso: el helper de invitar. Gris; en error si hay email escrito sin nombre guardado. */
  const showNameRequiredHelper = !loading && !hasSpaceName;
  const nameRequiredError = showNameRequiredHelper && email.trim().length > 0;

  const previewLabel = getSpaceOwnerLabel({
    ...(profile || {}),
    space_name: spaceNameDraft.trim() || null,
  });

  const handleSaveName = async () => {
    if (!nameDraft) {
      toast.error('Ingresá un nombre para el espacio.');
      return;
    }
    if (!canSaveName) return;
    if (spaceNameDraft.length > SPACE_NAME_MAX) {
      toast.error(`El nombre no puede superar ${SPACE_NAME_MAX} caracteres.`);
      return;
    }
    setSavingName(true);
    try {
      const res = await upsertMySpace({ space_name: nameDraft });
      if (!res._httpOk) {
        toast.error(res?.message || 'No se pudo guardar el nombre del espacio.');
        return;
      }
      const next = readSpaceProfile(res);
      setProfile((prev) => ({ ...(prev || {}), ...next }));
      setSpaceNameDraft(String(next.space_name || ''));
      toast.success('Nombre del espacio guardado.');
      onSpaceProfileSaved?.(next);
    } catch (error) {
      console.error('Espacios: POST /upsertmyspace falló', error);
      toast.error('No se pudo guardar el nombre del espacio.');
    } finally {
      setSavingName(false);
    }
  };

  const handleInvite = async () => {
    if (!hasSpaceName) {
      toast.error('Guardá un nombre para el espacio antes de invitar.');
      return;
    }
    if (!canInvite) {
      if (email.trim() && !isInviteEmailValid) {
        toast.error('Ingresá un email válido.');
      }
      return;
    }
    setInviting(true);
    try {
      const res = await inviteSpaceMember({ email: inviteEmail });
      if (!res._httpOk) {
        toast.error(res?.message || 'No se pudo invitar.');
        return;
      }
      toast.success(res._httpStatus === 200
        ? 'Ya estaba invitado a tu espacio.'
        : 'Invitación al espacio enviada.');
      setEmail('');
      await reload();
    } catch (error) {
      console.error('Espacios: POST /invitespacemember falló', error);
      toast.error('No se pudo invitar.');
    } finally {
      setInviting(false);
    }
  };

  const handleRemove = async (member) => {
    const memberEmail = String(member?.email || '').trim().toLowerCase();
    const userId = String(member?.user_id || '').trim();
    if (!memberEmail && !userId) return;
    const key = memberEmail || userId;

    const { isConfirmed } = await confirmDialog({
      title: 'Sacar del espacio',
      description: memberEmail
        ? `¿Sacar a ${memberEmail} de tu espacio?`
        : '¿Sacar a esta persona de tu espacio?',
      confirmLabel: 'Sacar',
      variant: 'danger',
    });
    if (!isConfirmed) return;

    setRemovingKey(key);
    try {
      const res = await deleteSpaceMember(
        memberEmail ? { email: memberEmail } : { user_id: userId },
      );
      if (!res._httpOk) {
        toast.error(res?.message || 'No se pudo sacar del espacio.');
        return;
      }
      toast.success('Persona sacada del espacio.');
      await reload();
    } catch (error) {
      console.error('Espacios: DELETE /deletespacemember falló', error);
      toast.error('No se pudo sacar del espacio.');
    } finally {
      setRemovingKey(null);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      PaperProps={{ sx: spaceMembersPaperSx }}
    >
      <DialogTitle sx={appModalTitleBarSx}>
        <Typography component="h2" sx={appModalTitleSx}>MI ESPACIO</Typography>
        <IconButton
          aria-label="Cerrar"
          onClick={handleClose}
          disabled={busy}
          sx={appModalCloseButtonSx}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>
      <Divider sx={appModalHeaderDividerSx} />
      <Typography sx={appModalSubtitleSx}>
        ¿Trabajás con otras personas? Invitalas a colaborar en tus registros y listas.
      </Typography>
      <DialogContent sx={appModalContentSx}>
        <Box sx={{ flexShrink: 0 }}>
          <Box sx={sectionLabelRowSx}>
            <Typography sx={{ ...sectionLabelSx, mb: 0 }}>
              Nombre del espacio
            </Typography>
            <Typography component="span" aria-hidden sx={requiredCaptionSx}>
              Requerido
            </Typography>
          </Box>
          <Box sx={fieldRowSx}>
            <TextField
              fullWidth
              size="small"
              placeholder="Ej. Oficina Norte"
              value={spaceNameDraft}
              onChange={(e) => setSpaceNameDraft(e.target.value.slice(0, SPACE_NAME_MAX))}
              disabled={loading || savingName}
              helperText={`Cómo lo ven los invitados: ${previewLabel}`}
              FormHelperTextProps={nameHelperTextProps}
              inputProps={{
                maxLength: SPACE_NAME_MAX,
                'aria-label': 'Nombre del espacio',
                'aria-required': true,
              }}
              sx={spaceFieldSx}
            />
            <Button
              variant="contained"
              onClick={handleSaveName}
              disabled={!canSaveName}
              sx={inlineActionButtonSx}
            >
              {savingName ? <CircularProgress size={18} sx={{ color: '#ffffff' }} /> : 'Guardar'}
            </Button>
          </Box>
        </Box>

        <Typography sx={{ ...sectionLabelSx, mt: 2.5 }}>
          Invitados
        </Typography>
        <Box sx={fieldRowSx}>
          <TextField
            fullWidth
            size="small"
            placeholder="email@ejemplo.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (canInvite) handleInvite();
              }
            }}
            disabled={inviting || loading}
            helperText={showNameRequiredHelper ? 'Guardá un nombre para poder invitar.' : ' '}
            // Error solo en el helper (sin borde rojo en el campo): el email no está mal.
            FormHelperTextProps={{ ...nameHelperTextProps, error: nameRequiredError }}
            inputProps={{ maxLength: 160, 'aria-label': 'Email a invitar' }}
            sx={spaceFieldSx}
          />
          <Button
            variant="contained"
            onClick={handleInvite}
            disabled={!canInvite}
            sx={inlineActionButtonSx}
          >
            {inviting ? <CircularProgress size={18} sx={{ color: '#ffffff' }} /> : 'Invitar'}
          </Button>
        </Box>

        <Box
          ref={scrollRef}
          aria-busy={loading || undefined}
          sx={{
            ...listWellSx,
            ...hiddenScrollbarSx,
            ...getScrollEdgeFadeSx(fadeTop, fadeBottom),
          }}
        >
          {loading ? <MembersSkeleton /> : null}
          {!loading && members.length === 0 ? (
            <Box sx={emptyStateSx}>
              <PeopleOutlineIcon sx={{ fontSize: 34, color: 'rgba(251,251,251,0.45)' }} />
              <Typography sx={{ ...memberNameSx, whiteSpace: 'normal' }}>
                Todavía no invitaste a nadie
              </Typography>
              <Typography sx={{ ...memberMetaSx, maxWidth: 320 }}>
                Invitá por email para que vean tu espacio.
              </Typography>
            </Box>
          ) : null}
          {!loading && members.map((member) => {
            const key = memberKey(member);
            const rowBusy = removingKey === key;
            return (
              <Box key={key} sx={memberCardSx}>
                <Box aria-hidden sx={memberAvatarSx}>{memberInitial(member)}</Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                    <Typography sx={{ ...memberNameSx, flex: 1, minWidth: 0 }}>
                      {member.email || member.user_id}
                    </Typography>
                  </Box>
                  <Typography sx={memberMetaSx}>Editor del espacio</Typography>
                </Box>
                {rowBusy ? (
                  <CircularProgress size={16} sx={{ color: colors.primary, flexShrink: 0 }} />
                ) : (
                  <Tooltip title="Sacar del espacio" arrow>
                    <span>
                      <IconButton
                        size="small"
                        aria-label="Sacar del espacio"
                        onClick={() => handleRemove(member)}
                        disabled={inviting || savingName}
                        sx={removeButtonSx}
                      >
                        <PersonRemoveOutlinedIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Box>
            );
          })}
        </Box>
      </DialogContent>
      <DialogActions sx={appModalActionsSx}>
        <Button
          variant="outlined"
          onClick={handleClose}
          disabled={busy}
          sx={appModalSecondaryButtonSx}
        >
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
